import React, { useState, useEffect, useCallback, useMemo } from "react"; // ✅ Added useMemo
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  IconButton,
  Badge,
  TextField,
  Alert,
  Paper,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  useMediaQuery,
  Tooltip, // ✅ Added Tooltip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Slide from "@mui/material/Slide";
import {
  AddShoppingCart,
  Delete,
  ArrowBack,
  ShoppingBasket,
  Search,
  Clear,
  Close,
  HourglassTop, // ✅ Added HourglassTop Icon
} from "@mui/icons-material";

import DynamicTable from "../Dynamic/DynamicTable";
import DynamicModal from "../Dynamic/DynamicModal";

import { useUser } from "../../Context/UserContext";
import { GetApi, PostApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";

const RequestSpecies = () => {
  const { centerId, farmerId } = useParams();
  const navigate = useNavigate();
  const { token } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State
  const [stockList, setStockList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [openCartModal, setOpenCartModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [existingRequests, setExistingRequests] = useState([]); // ✅ Added to store farmer's previous orders

  // Search State
  const [searchTerm, setSearchTerm] = useState("");

  // Toast State
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  // Delete Confirmation State
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchStock = useCallback(async () => {
    try {
      setLoading(true);
      const res = await GetApi(
        `${API_URLS.species}?production_center_id=${centerId}`,
        token
      );
      setStockList(res.results || []);
    } catch (err) {
      console.error("Failed to fetch stock", err);
    } finally {
      setLoading(false);
    }
  }, [centerId, token]);

  // ✅ ADDED: Fetch farmer's existing orders to check for pending items
  const fetchExistingRequests = useCallback(async () => {
    if (!farmerId) return;
    try {
      const res = await GetApi(`/users/farmer-request/?user_id=${farmerId}`, token);
      setExistingRequests(res.results || []);
    } catch (err) {
      console.error("Failed to fetch existing requests", err);
    }
  }, [farmerId, token]);

  useEffect(() => {
    fetchStock();
    fetchExistingRequests(); // ✅ ADDED
    return () => {
      setCart([]);
      setSearchTerm("");
    };
  }, [fetchStock, fetchExistingRequests]); // ✅ ADDED

  // ✅ ADDED: Memoized list of species IDs that are locked (in pending/order-placed status)
  const blockedSpeciesIds = useMemo(() => {
    const ids = new Set();
    existingRequests.forEach((order) => {
      // Check if the order belongs to the CURRENT production center
      if (String(order.production_center_id) === String(centerId)) {
        // If the order itself is pending or order-placed, all species inside it are locked
        if (order.order_status === "pending" || order.order_status === "order-placed") {
          order.requested_items.forEach((item) => {
            ids.add(item.species_id);
          });
        }
      }
    });
    return ids;
  }, [existingRequests, centerId]);

  // Filter Logic
  const filteredStockList = stockList.filter((item) => {
    if (!searchTerm.trim()) return true;
    const lowerSearch = searchTerm.toLowerCase();
    return (
      item.species_name?.toLowerCase().includes(lowerSearch) ||
      item.species_name_tamil?.toLowerCase().includes(lowerSearch) ||
      item.lot_number?.toLowerCase().includes(lowerSearch)
    );
  });

  // --- Cart Handlers ---
  const handleAddToCart = (item) => {
    // ✅ ADDED: Double security check to prevent adding if blocked
    if (blockedSpeciesIds.has(item.species_id)) return; 

    const exists = cart.find((c) => c.stockId === item.id);
    if (exists) return;
    setCart([
      ...cart,
      {
        stockId: item.id,
        species_id: item.species_id,
        species_name: item.species_name,
        price: parseFloat(item.price_per_sapling),
        maxAvailable: item.saplings_available,
        quantity: 1,
      },
    ]);
  };

  const handleRemoveFromCart = (stockId) =>
    setCart(cart.filter((item) => item.stockId !== stockId));

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteOpen(true);
  };

  const confirmDeleteItem = () => {
    if (itemToDelete) {
      handleRemoveFromCart(itemToDelete.stockId);
    }
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const cancelDeleteItem = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const handleQuantityChange = (stockId, value) => {
    const val = parseInt(value) || 0;
    setCart(
      cart.map((item) =>
        item.stockId === stockId
          ? { ...item, quantity: Math.min(val, item.maxAvailable) }
          : item
      )
    );
  };

  const isInCart = (stockId) => cart.some((item) => item.stockId === stockId);

  const handleBackNavigation = () => {
    if (cart.length > 0) {
      setOpenConfirmDialog(true);
    } else {
      navigate(-1);
    }
  };

  const confirmLeave = () => {
    setCart([]);
    setOpenConfirmDialog(false);
    navigate(-1);
  };
  const cancelLeave = () => {
    setOpenConfirmDialog(false);
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;
    const invalid = cart.find((item) => !item.quantity || item.quantity <= 0);
    if (invalid) {
      setError(`Please enter a valid quantity for ${invalid.species_name}`);
      return;
    }

    const payload = {
      farmer_id: farmerId,
      production_center_id: parseInt(centerId),
      items: cart.map((item) => ({
        stock_id: item.stockId,
        species_id: item.species_id,
        quantity: item.quantity,
        price_per_unit: item.price,
        production_center_id: centerId,
      })),
    };

    try {
      setSubmitting(true);
      setError("");
      await PostApi(`${API_URLS.allocations}`, payload, token);
      
      setOpenCartModal(false);
      setCart([]);
      
      setToastMessage("✅ Order placed successfully!");
      setToastSeverity("success");
      setToastOpen(true);

      // ✅ ADDED: Refresh existing requests so newly added species gets locked immediately in UI
      fetchExistingRequests();

    } catch (err) {
      setToastMessage("❌ Failed to place order. Please try again.");
      setToastSeverity("error");
      setToastOpen(true);
      
      setError(err.response?.data?.error || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const getTotalPrice = () =>
    cart.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2);

  // Columns for Desktop Table only
  const cartColumns = [
    {
      field: "species_name",
      headerName: "Species",
      render: (row) => (
        <Typography fontWeight={600} fontSize="0.85rem">
          {row.species_name}
        </Typography>
      ),
    },
    { field: "maxAvailable", headerName: "Avail", align: "center" },
    {
      field: "price",
      headerName: "Price",
      render: (row) => <Typography fontSize="0.85rem">₹{row.price}</Typography>,
    },
    {
      field: "quantity",
      headerName: "Qty",
      render: (row) => (
        <TextField
          type="number"
          size="small"
          variant="outlined"
          value={row.quantity}
          onChange={(e) => handleQuantityChange(row.stockId, e.target.value)}
          inputProps={{
            min: 1,
            max: row.maxAvailable,
            style: { width: 60, textAlign: "center" },
          }}
        />
      ),
    },
    {
      field: "total",
      headerName: "Total",
      render: (row) => (
        <Typography fontWeight={700} fontSize="0.85rem">
          ₹{(row.price * row.quantity).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "",
      render: (row) => (
        <IconButton
          color="error"
          onClick={() => handleDeleteClick(row)}
          size="small"
          sx={{ "&:hover": { backgroundColor: "#ffebee" } }}
        >
          <Delete fontSize="small" />
        </IconButton>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "calc(100% - 50px)",
        mx: "auto",
        pb: 10,
        bgcolor: "#f9f9f9",
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      <style>
        {`
          .species-card-item {
            width: 100% !important;
            max-width: 100% !important;
          }
          @media (min-width: 600px) {
            .species-card-item {
              width: calc(50% - 8px) !important;
              max-width: calc(50% - 8px) !important;
            }
          }
          @media (min-width: 900px) {
            .species-card-item {
              width: calc(50% - 8px) !important;
              max-width: calc(50% - 8px) !important;
            }
          }
          @media (min-width: 1200px) {
            .species-card-item {
              width: calc(25% - 12px) !important;
              max-width: calc(25% - 12px) !important;
            }
          }
        `}
      </style>

      {/* Header with Search on the Right */}
      <Box sx={{ mb: 3, pt: 0.5, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
          <IconButton onClick={handleBackNavigation}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant={{ xs: "h6", sm: "h5" }} fontWeight={700}>
              Select Species
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: { xs: "100%", sm: 220, md: 260 }, flexShrink: 0 }}>
          <TextField
            size="small"
            placeholder="Search species..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1 }}>
                  <Search sx={{ color: "#2e7d32", fontSize: "1.1rem" }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")} sx={{ color: "#555", p: 0.3, "&:hover": { color: "#2e7d32" } }}>
                    <Clear sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { fontSize: "0.85rem", py: 0.2 },
            }}
            sx={{
              bgcolor: "white", borderRadius: 2, boxShadow: "0 1px 4px rgb(0 0 0 / 0.06)",
              "& fieldset": { borderColor: "#a5d6a7", borderWidth: 1, borderRadius: 2 },
              "&:hover fieldset": { borderColor: "#388e3c" },
              "&.Mui-focused fieldset": { borderColor: "#2e7d32", borderWidth: 1.5 },
            }}
          />
        </Box>
      </Box>

      {/* Stock Grid */}
      {filteredStockList.length === 0 ? (
        <Box textAlign="center" mt={5}>
          <Typography variant="h6" sx={{ color: "#555", mb: 1 }}>
            {searchTerm ? " No species found" : "No stock available at this center."}
          </Typography>
          {searchTerm && (
            <Typography variant="body2" color="text.secondary">
              No results match "<strong>{searchTerm}</strong>". Try a different search term.
            </Typography>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredStockList.map((item) => {
            // ✅ ADDED: Check if this exact species is locked by an active order
            const isBlocked = blockedSpeciesIds.has(item.species_id);
            const inCart = isInCart(item.id);

            return (
              <Grid item className="species-card-item" key={item.id}>
                <Card sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "transform 0.2s, box-shadow 0.2s", "&:hover": { transform: "translateY(-4px)", boxShadow: "0 6px 16px rgba(0,0,0,0.12)" }, boxSizing: "border-box", opacity: isBlocked ? 0.7 : 1 }}>
                  <Box sx={{ height: { xs: 140, sm: 160, md: 180 }, width: "100%", bgcolor: "#f5f5f5", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0" }}>
                    {/* ✅ ADDED: Greyscale image if blocked */}
                    <img src={item.image ? `/api/${item.image.replace(/\\/g, '/')}` : "/img3.jpg"} alt={item.species_name} style={{ width: "100%", height: "100%", objectFit: "cover", filter: isBlocked ? "grayscale(100%)" : "none" }} />
                    <Box sx={{ position: "absolute", top: 10, right: 10, bgcolor: "rgba(255, 255, 255, 0.95)", color: item.saplings_available > 0 ? "#2e7d32" : "#c62828", px: 1.5, py: 0.5, borderRadius: 2, fontSize: "0.75rem", fontWeight: 700, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                      saplings Available : {item.saplings_available}
                    </Box>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1, lineHeight: 1.4, color: "#333", display: "-webkit-box", overflow: "hidden", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                      {item.species_name} <span style={{ color: "#777", fontWeight: 400 }}>/</span> {item.species_name_tamil}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, color: "text.secondary", fontSize: "0.85rem", mb: 2 }}>
                      <Typography variant="body2">Lot: {item.lot_number}</Typography>
                      <Typography variant="body2" sx={{ color: "#ccc" }}>|</Typography>
                      <Typography variant="body2">Age: {item.sapling_age}</Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: { xs: 1, sm: 1.5 }, pt: 0, justifyContent: "space-between", mt: "auto" }}>
                    <Typography variant="h6" color="primary" fontWeight={800} sx={{ mb: 0 }}>₹ {item.price_per_sapling}</Typography>
                    
                    {/* ✅ UPDATED: Conditional Button Rendering */}
                    {isBlocked ? (
                      <Tooltip title="Already requested for this center" arrow placement="top">
                        <Button 
                          variant="outlined" 
                          startIcon={<HourglassTop />} 
                          disabled 
                          sx={{ 
                            borderRadius: 2, px: { xs: 2, sm: 4 }, py: 1, fontWeight: 700, 
                            fontSize: { xs: "0.85rem", sm: "1rem" }, 
                            borderColor: "#bdbdbd", color: "#757575", minWidth: "auto", height: "40px" 
                          }}
                        >
                          PENDING
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button 
                        variant={inCart ? "outlined" : "contained"} 
                        startIcon={<AddShoppingCart />} 
                        onClick={() => handleAddToCart(item)} 
                        disabled={item.saplings_available === 0 || inCart} 
                        sx={{ 
                          borderRadius: 2, px: { xs: 2, sm: 4 }, py: 1, fontWeight: 700, 
                          fontSize: { xs: "0.85rem", sm: "1rem" }, 
                          bgcolor: inCart ? "transparent" : "#ffc022", 
                          color: inCart ? "#2e7d32" : "black", 
                          "&:hover": { bgcolor: inCart ? "#e8f5e9" : "#ffab00", borderColor: "#2e7d32" }, 
                          borderColor: "#2e7d32", minWidth: "auto", height: "40px" 
                        }}
                      >
                        {inCart ? "ADDED" : "ADD"}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Floating Cart Button */}
      {cart.length > 0 && (
        <Paper elevation={8} sx={{ position: "fixed", bottom: 24, right: 24, borderRadius: 50, zIndex: 1000 }}>
          <IconButton onClick={() => setOpenCartModal(true)} sx={{ p: 2, bgcolor: "#005500", color: "#fff", "&:hover": { bgcolor: "#004400" } }}>
            <Badge badgeContent={cart.length} color="error">
              <ShoppingBasket sx={{ fontSize: 30 }} />
            </Badge>
          </IconButton>
        </Paper>
      )}

      {/* ================================================================== */}
      {/* NEW MOBILE-FIRST ORDER MODAL */}
      {/* ================================================================== */}
      <Dialog
        open={openCartModal}
        onClose={() => setOpenCartModal(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            display: "flex",
            flexDirection: "column",
            maxHeight: isMobile ? "100%" : "85vh",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #eee", pb: 1, bgcolor: "#fafafa", pr: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
              Review Order (ID: {centerId})
            </Typography>
            
            <IconButton 
              onClick={() => setOpenCartModal(false)} 
              sx={{ 
                color: "#616161", 
                "&:hover": { color: "#d32f2f", bgcolor: "#ffebee" } 
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Modal Body */}
        <DialogContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, overflowY: "auto", bgcolor: "#f9f9f9" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {cart.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5, color: "#999" }}>
              <ShoppingBasket sx={{ fontSize: 48, mb: 1, color: "#e0e0e0" }} />
              <Typography>Cart is empty</Typography>
            </Box>
          ) : (
            <>
              {/* DESKTOP VIEW: Standard Table (Hidden on Mobile) */}
              {!isMobile && (
                <Box sx={{ overflowX: "auto" }}>
                  <DynamicTable
                    data={cart}
                    columns={cartColumns}
                    disablePagination={true}
                    showBorder={false}
                    enableSearch={false}
                    emptyMessage="Cart is empty"
                  />
                </Box>
              )}

              {/* MOBILE VIEW: Card Layout (Hidden on Desktop) */}
              {isMobile && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {cart.map((row) => (
                    <Paper key={row.stockId} elevation={0} sx={{ p: 1.5, border: "1px solid #e0e0e0", borderRadius: 2.5, bgcolor: "#fff" }}>
                      {/* Top Row: Name & Delete */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography fontWeight={700} fontSize="0.9rem" sx={{ color: "#333" }}>
                          {row.species_name}
                        </Typography>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(row)}
                          sx={{ "&:hover": { backgroundColor: "#ffebee" } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Middle Row: Avail & Price */}
                      <Box sx={{ display: "flex", gap: 2, mb: 1.5, fontSize: "0.8rem", color: "#666" }}>
                        <Typography variant="body2">Avail: {row.maxAvailable}</Typography>
                        <Typography variant="body2">Price: ₹{row.price}</Typography>
                      </Box>

                      {/* Bottom Row: Qty Input & Total */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1.5, borderTop: "1px dashed #e0e0e0" }}>
                        <TextField
                          type="number"
                          size="small"
                          variant="outlined"
                          value={row.quantity}
                          onChange={(e) => handleQuantityChange(row.stockId, e.target.value)}
                          inputProps={{
                            min: 1,
                            max: row.maxAvailable,
                            style: { width: 60, textAlign: "center", fontSize: "0.9rem" },
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                        />
                        <Typography fontWeight={800} fontSize="1.1rem" color="primary.main">
                          ₹{(row.price * row.quantity).toFixed(2)}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* Modal Footer (Sticky Grand Total & Button) */}
        <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: "#ffffff", borderTop: "1px solid #eee" }}>
          <Box sx={{ p: 2, mb: 2, bgcolor: "#e8f5e9", borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={700}>Grand Total:</Typography>
            <Typography variant="h6" fontWeight={800} color="primary">
              ₹ {getTotalPrice()}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
            <Button onClick={() => setOpenCartModal(false)} color="inherit" sx={{ display: { xs: "none", sm: "block" } }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handlePlaceOrder} 
              disabled={submitting || cart.length === 0}
              fullWidth={isMobile}
              sx={{ py: { xs: 1.2, sm: 1 }, fontWeight: 700, bgcolor: "#005500", "&:hover": { bgcolor: "#003300" } }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : "Place Order"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Leave Page Confirmation Dialog */}
      <DynamicModal
        open={openConfirmDialog}
        onClose={cancelLeave}
        onConfirm={confirmLeave}
        confirmText="Yes, Leave"
        cancelText="Stay Here"
        title="Leave Page?"
        contentText="Orders can only be done within one production center. If you leave now, the items in your cart will be removed. Are you sure you want to leave?"
        maxWidth="xs"
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteOpen} onClose={cancelDeleteItem} PaperProps={{ sx: { borderRadius: 2, maxWidth: 400 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#d32f2f", display: "flex", alignItems: "center", gap: 1 }}>
          <Delete fontSize="small" />
          Remove Item
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{itemToDelete?.species_name}</strong> from your cart?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancelDeleteItem} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button onClick={confirmDeleteItem} color="error" variant="contained" startIcon={<Delete />}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} 
        TransitionComponent={(props) => <Slide {...props} direction="left" />} 
        sx={{ top: { xs: 70, sm: 80 } }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          variant="filled"
          sx={{ 
            width: '100%', 
            borderRadius: 2, 
            fontWeight: 700, 
            fontSize: '0.9rem', 
            alignItems: 'center', 
            '& .MuiAlert-icon': { fontSize: '22px' }
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestSpecies;